import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import { preferencesClient } from '@/api/clients/preferencesClient';
import { NetworkError, ServerError } from '@/api/errors';
import { type UserPreference } from '@/api/types/userPreferenceTypes';
import { useUserInfo } from '@/features/auth/hooks/useUserInfo';
import { isTokenAuthenticated } from '@/features/auth/isTokenAuthenticated';

export const useUserPreferencesQueryKey = 'useUserPreferencesQuery';

/**
 * @param fillProperties - User properties to be filled, if not provided, all properties will be filled. To avoid parsing unnecessary data.
 */
export function useUserPreferencesQuery(options?: { staleTime?: number }): UseQueryResult<UserPreference> {
  const { accessToken } = useUserInfo();
  const isAuthenticated = isTokenAuthenticated(accessToken);

  return useQuery({
    retry: (failureCount, error) => {
      switch (error.constructor) {
        case NetworkError:
        case ServerError:
          return failureCount < 3;
      }

      return false;
    },
    throwOnError: false,
    queryKey: [useUserPreferencesQueryKey, accessToken],
    queryFn: async () => {
      const userPreference = await preferencesClient.getUserPreference();

      return userPreference;
    },
    ...(options?.staleTime !== undefined ? { staleTime: options.staleTime } : {}), // Optionally override staleTime
    // Should only be enabled for authenticated users
    enabled: isAuthenticated,
  });
}
